// 상담 챗봇 훅 계층 — 페이지와 api/counsel.ts 사이의 단일 경계.
//
// 페이지는 이 파일만 import 한다. 쿼리 키·캐시 무효화 전략을 여기 모은다(hooks/crm.ts 와 동일 규약).
//
// ## 폴링 주기를 왜 이렇게 잡았나
//
// 고객은 우리 화면 밖(자기 폰)에서 말을 건다. 서버가 먼저 알려주는 통로(웹소켓)를 안 깔았으므로
// 화면이 주기적으로 물어보는 수밖에 없다. 목록은 12초, 열어 둔 대화의 변경 표식은 4초 —
// 표식이 달라졌을 때만 상세 본문을 다시 읽는다. 상담은 사람이 답하는 속도라 이 정도면
// "실시간처럼" 느껴지면서도 매 tick마다 메시지 전체를 다시 싣지 않는다.
// (⚠️ refetchIntervalInBackground 를 켜지 않는다. 탭을 방치한 채 하루 종일 켜 두는 사람이
//  많아서, 배경 폴링을 켜면 아무도 안 보는 화면이 계속 서버를 두드린다.)

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/counsel";
import type {
  CounselKnowledgeInput,
  CounselLearningFilter,
} from "../../shared/counsel";
import type { CounselFeedbackVerdict } from "../../shared/counsel-feedback";

export const counselKeys = {
  all: () => ["counsel"] as const,
  channel: () => ["counsel", "channel"] as const,
  conversations: () => ["counsel", "conversations"] as const,
  unansweredCount: () => ["counsel", "unanswered-count"] as const,
  conversation: (id: number) => ["counsel", "conversation", id] as const,
  conversationRevision: (id: number) =>
    ["counsel", "conversation-revision", id] as const,
  draftContext: (draftId: number) => ["counsel", "draft-context", draftId] as const,
  knowledge: () => ["counsel", "knowledge"] as const,
  instructions: () => ["counsel", "instructions"] as const,
  learningCases: (filter: CounselLearningFilter) =>
    ["counsel", "learning", filter] as const,
  learningCounts: () => ["counsel", "learning", "counts"] as const,
  pendingFeedback: () => ["counsel", "feedback", "pending"] as const,
};

// ─── Queries ─────────────────────────────────────────

export function useCounselChannel() {
  return useQuery({
    queryKey: counselKeys.channel(),
    queryFn: api.fetchCounselChannel,
  });
}

export function useCounselConversations() {
  return useQuery({
    queryKey: counselKeys.conversations(),
    queryFn: api.fetchCounselConversations,
    refetchInterval: 12_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/** 전 화면 LNB용. 합의한 반영 속도인 1분 주기이며 숨은 탭에서는 멈춘다. */
export function useCounselUnansweredCount(enabled = true) {
  return useQuery({
    queryKey: counselKeys.unansweredCount(),
    queryFn: api.fetchCounselUnansweredCount,
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useCounselConversation(id: number | null) {
  const detail = useQuery({
    queryKey: counselKeys.conversation(id ?? 0),
    queryFn: () => api.fetchCounselConversation(id as number),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });
  const revision = useQuery({
    queryKey: counselKeys.conversationRevision(id ?? 0),
    queryFn: () => api.fetchCounselConversationRevision(id as number),
    enabled: !!id,
    // 4초마다 전체 대화를 다시 싣지 않는다. 새 메시지·초안·CRM·슬롯·평가 중 무엇이든
    // 바뀌면 revision이 달라지고, 아래 effect가 그때 한 번만 상세를 갱신한다.
    refetchInterval: 4_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
  const {
    data: detailData,
    isFetching: isDetailFetching,
    refetch: refetchDetail,
  } = detail;

  useEffect(() => {
    if (!id || isDetailFetching) return;
    const detailRevision = detailData?.revision;
    const polledRevision = revision.data?.revision;
    if (detailRevision && polledRevision && detailRevision !== polledRevision) {
      void refetchDetail();
    }
  }, [id, detailData?.revision, isDetailFetching, refetchDetail, revision.data?.revision]);

  return detail;
}

/**
 * "이 초안에 무엇이 실렸나" — 🔎 왜 이렇게 답했나를 펼칠 때만 부른다.
 *
 * 초안 하나의 주입 내역은 **한 번 만들어지면 안 변한다**(다시 만들면 새 초안 id 가 된다).
 * 그래서 폴링하지 않고, 캐시도 넉넉히 잡아 같은 초안을 여닫아도 서버를 다시 두드리지 않는다.
 */
export function useCounselDraftContext(draftId: number | null, enabled = true) {
  return useQuery({
    queryKey: counselKeys.draftContext(draftId ?? 0),
    queryFn: () => api.fetchCounselDraftContext(draftId as number),
    enabled: !!draftId && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCounselKnowledge() {
  return useQuery({
    queryKey: counselKeys.knowledge(),
    queryFn: api.fetchCounselKnowledge,
  });
}

// ─── Mutations ───────────────────────────────────────

export function useUpdateCounselChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Parameters<typeof api.updateCounselChannel>[0]) =>
      api.updateCounselChannel(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: counselKeys.channel() }),
  });
}

export function useRotateCounselToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.rotateCounselToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: counselKeys.channel() }),
  });
}

/** 답변 전송 — 성공 시 대화·목록을 갱신한다(초안 상태도 함께 바뀌므로 상세를 다시 읽는다). */
export function useSendCounselMessage() {
  const qc = useQueryClient();
  return useMutation({
    // 호출부가 자체 토스트로 안내 → 전역 에러 토스트는 끈다(중복 방지).
    meta: { silentError: true },
    mutationFn: (v: {
      id: number;
      text: string;
      draftId?: number | null;
      images?: File[];
    }) => api.sendCounselMessage(v.id, v.text, v.draftId, v.images),
    onSuccess: (_message, v) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(v.id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
      qc.invalidateQueries({ queryKey: counselKeys.unansweredCount() });
    },
  });
}

/**
 * 답변 평가(👍/👎) — 내부자용.
 *
 * ## 왜 화면을 먼저 바꾸나
 *
 * 서버 응답만 기다리면 네트워크 왕복 동안 버튼이 안 눌린 채로 있다. 그 사이 사람은
 * "안 눌렸나?" 하고 또 누른다. 그래서 캐시를 먼저 고치고(낙관적 갱신) 서버에 보낸 뒤,
 * 실패하면 되돌린다.
 *
 * ⚠️ 평가는 챗봇을 즉시 바꾸지 않는다 — 배치가 읽어 후보를 만들 뿐이다. 그래서 성공해도
 *    지식·예시 캐시를 건드리지 않는다(건드리면 "지금 배웠다"는 잘못된 인상을 준다).
 */
export function useRateCounselMessage() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (v: {
      conversationId: number;
      messageId: number;
      verdict: CounselFeedbackVerdict | null;
      reasons?: string[];
      reasonText?: string;
    }) =>
      api.rateCounselMessage(v.conversationId, v.messageId, v.verdict, {
        reasons: v.reasons,
        reasonText: v.reasonText,
      }),
    onMutate: async (v) => {
      const key = counselKeys.conversation(v.conversationId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<api.CounselConversationDetail>(key);
      if (prev) {
        const myFeedback = { ...prev.myFeedback };
        if (v.verdict) myFeedback[v.messageId] = v.verdict;
        else delete myFeedback[v.messageId];
        qc.setQueryData(key, { ...prev, myFeedback });
      }
      return { prev, key };
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(v.conversationId) });
      qc.invalidateQueries({ queryKey: counselKeys.pendingFeedback() });
    },
  });
}

/** 아직 학습에 안 실린 평가 건수 — 인박스가 "가르칠 게 쌓였다"를 알리는 값. */
export function useCounselPendingFeedback(enabled = true) {
  return useQuery({
    queryKey: counselKeys.pendingFeedback(),
    queryFn: api.fetchCounselPendingFeedback,
    enabled,
    // 자주 볼 값이 아니다 — 평가가 쌓이는 속도는 하루 단위다.
    staleTime: 60_000,
  });
}

// ─── 테스트 상담 ────────────────────────────────────────────
//
// 답을 기다렸다가 받는 요청이라 다른 뮤테이션보다 오래 걸린다(초안 생성 시간만큼).
// 화면은 isPending 으로 "챗봇이 답하는 중…"을 보여준다.

export function useCreateCounselTestConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name?: string) => api.createCounselTestConversation(name),
    onSuccess: (conversation) => {
      // 만든 방을 **목록 캐시에 즉시** 얹는다. 서버 재조회를 기다리면 그 짧은 사이에
      // 화면이 "이건 테스트방인지" 알 수 없어, 대기 초안 자동 확인창이 방금 연 방에서
      // 선택을 빼앗아 간다(폴링이 12초마다 돈다).
      qc.setQueryData<api.CounselConversationsResult>(counselKeys.conversations(), (old) =>
        old
          ? { ...old, conversations: [conversation, ...old.conversations] }
          : old,
      );
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
    },
  });
}

export function useSendCounselTestMessage() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (v: { id: number; text: string }) =>
      api.sendCounselTestMessage(v.id, v.text),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(v.id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
    },
  });
}

export function useDeleteCounselTestConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteCounselTestConversation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: counselKeys.conversations() }),
  });
}

export function useDiscardCounselDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; draftId: number }) =>
      api.discardCounselDraft(v.id, v.draftId),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(v.id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
      qc.invalidateQueries({ queryKey: counselKeys.unansweredCount() });
    },
  });
}

export function useRedraftCounselAnswer() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (id: number) => api.redraftCounselAnswer(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
      qc.invalidateQueries({ queryKey: counselKeys.unansweredCount() });
    },
  });
}

/**
 * 챗봇에게 지시한다 — 인박스 옆 «AI에게 지시» 창.
 *
 * 성공하면 지식 목록을 다시 읽는다. 지시가 규칙으로 쌓이는 게 이 기능의 전부라,
 * 방금 한 말이 목록에 바로 보여야 사장님이 "정말 반영됐구나"를 확인할 수 있다.
 */
export function useInstructCounselBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      text,
      history,
    }: {
      id: number;
      text: string;
      history?: api.CounselTrainerTurn[];
    }) => api.instructCounselBot(id, text, history ?? []),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: counselKeys.instructions() });
      qc.invalidateQueries({ queryKey: counselKeys.knowledge() });
    },
  });
}

/** 지시로 쌓인 규칙 목록. 창을 열었을 때만 읽는다(평소엔 볼 일이 없다). */
export function useCounselInstructions(enabled = true) {
  return useQuery({
    queryKey: counselKeys.instructions(),
    queryFn: api.listCounselInstructions,
    enabled,
  });
}

export function useCloseCounselConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.closeCounselConversation(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
      qc.invalidateQueries({ queryKey: counselKeys.unansweredCount() });
    },
  });
}

export function useReopenCounselConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.reopenCounselConversation(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: counselKeys.conversation(id) });
      qc.invalidateQueries({ queryKey: counselKeys.conversations() });
      qc.invalidateQueries({ queryKey: counselKeys.unansweredCount() });
    },
  });
}

// ─── 지식 ────────────────────────────────────────────
//
// ⚠️ 카드를 하나 고치면 **현황판 집계도 같이 낡는다.** "챗봇이 배운 것" 화면의 신호등(🟢🟡🔴)과
//    "이번 주 N건 배움"은 이제 전부 서버 집계(GET /knowledge/overview)를 그대로 그리기 때문에,
//    카드를 추가해 놓고 집계를 안 다시 읽으면 방금 채운 주제가 계속 🔴 "안 배움"으로 남는다.
//    그래서 지식 CRUD 뒤에는 목록과 집계를 함께 무효화한다.
//
//    (키를 hooks/counsel-training.ts 에서 가져오지 않고 여기 적은 이유: 그쪽이 이미 이 파일의
//     counselKeys 를 가져다 쓰고 있어서, 반대로 가져오면 두 파일이 서로를 부르는 고리가 된다.)
const LEARNING_OVERVIEW_KEY = ["counsel-training", "overview"] as const;

function invalidateKnowledge(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: counselKeys.knowledge() });
  qc.invalidateQueries({ queryKey: LEARNING_OVERVIEW_KEY });
}

export function useCreateCounselKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CounselKnowledgeInput) => api.createCounselKnowledge(input),
    onSuccess: () => invalidateKnowledge(qc),
  });
}

export function useUpdateCounselKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; patch: Partial<CounselKnowledgeInput> }) =>
      api.updateCounselKnowledge(v.id, v.patch),
    onSuccess: () => invalidateKnowledge(qc),
  });
}

export function useDeleteCounselKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteCounselKnowledge(id),
    onSuccess: () => invalidateKnowledge(qc),
  });
}

// ─── 학습 (사람이 고른 사례 → 지식 규칙) ────────────────────
//
// ⚠️ 학습 화면은 **폴링하지 않는다.** 후보는 담당자가 답을 보낼 때만 늘어나는 느린 목록이라,
//    자동 갱신이 오히려 방해가 된다(체크해 둔 것이 새로고침에 밀려 사라지면 짜증만 난다).

export function useCounselLearningCases(filter: CounselLearningFilter) {
  return useQuery({
    queryKey: counselKeys.learningCases(filter),
    queryFn: () => api.fetchCounselLearningCases(filter),
  });
}

/** 인박스 상단 안내용 — "가르칠 게 N건 쌓였다". */
// 인박스에서 "고쳐서 보낸 답 N건"을 알리는 데만 쓴다. 그 안내는 관리자에게만 보이므로
// 직원 화면에서는 아예 부르지 않는다(안 그러면 쓰지도 않을 값을 매번 받아 온다).
export function useCounselLearningCounts(enabled = true) {
  return useQuery({
    queryKey: counselKeys.learningCounts(),
    queryFn: api.fetchCounselLearningCounts,
    enabled,
  });
}

export function usePickCounselLearningCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { draftId: number; picked: boolean }) =>
      api.pickCounselLearningCase(v.draftId, v.picked),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["counsel", "learning"] });
    },
  });
}

/** 규칙 초안 제안 — 저장은 하지 않으므로 캐시를 건드리지 않는다. */
export function useSuggestCounselLearningRule() {
  return useMutation({
    meta: { silentError: true },
    mutationFn: (draftIds: number[]) => api.suggestCounselLearningRule(draftIds),
  });
}

/** 규칙 저장 — 지식 목록과 학습 후보가 함께 바뀐다(반영된 사례는 목록에서 표시가 달라진다). */
export function usePromoteCounselLearningRule() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silentError: true },
    mutationFn: (v: { draftIds: number[]; input: CounselKnowledgeInput }) =>
      api.promoteCounselLearningRule(v.draftIds, v.input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["counsel", "learning"] });
      invalidateKnowledge(qc);
    },
  });
}
